import type Phaser from "phaser";
import { ArtPipeline } from "../../../art";
import { hexToColor } from "../../../data/infinite-war-catalog";
import {
  tileContested,
  tileOwner,
  type InfiniteWarState,
} from "../infinite-war-rules";
import { ART_PACK, FIELD } from "./layout";
import { resolveDominionTileLook } from "./tiles-look";

export type { DominionTileLook } from "./tiles-look";
export { resolveDominionTileLook } from "./tiles-look";

type TilePool = {
  images: Phaser.GameObjects.Image[];
  hooked: boolean;
};

const pools = new WeakMap<Phaser.Scene, TilePool>();

function clearPool(pool: TilePool): void {
  for (const img of pool.images) img.destroy();
  pool.images = [];
}

function poolFor(scene: Phaser.Scene): TilePool {
  let pool = pools.get(scene);
  if (!pool) {
    pool = { images: [], hooked: false };
    pools.set(scene, pool);
  }
  if (!pool.hooked) {
    pool.hooked = true;
    const onClear = () => clearPool(pool!);
    // String event names — avoid value-import of Phaser in this module.
    scene.events.on("shutdown", onClear);
    scene.events.on("destroy", onClear);
  }
  return pool;
}

/**
 * Present sticky tile ownership with authored faction sprites (heaven / death).
 * Keeps the scene call `drawDominionTiles(g, state)` — scene comes from `g.scene`.
 * Low-chroma tint + alpha so units stay the focus (no neon fills).
 */
export function drawDominionTiles(
  g: Phaser.GameObjects.Graphics,
  state: InfiniteWarState,
): void {
  const scene = g.scene;
  const pool = poolFor(scene);
  const tileW = FIELD.w / FIELD.cols;
  const tileH = FIELD.h / FIELD.rows;
  const needed = FIELD.cols * FIELD.rows;
  let i = 0;
  for (let row = 0; row < FIELD.rows; row += 1) {
    for (let col = 0; col < FIELD.cols; col += 1) {
      const look = resolveDominionTileLook(
        tileOwner(state, col, row),
        tileContested(state, col, row),
        col,
        row,
      );
      const { key } = ArtPipeline.ensureAnimFrame(
        scene,
        ART_PACK,
        look.spriteId,
        "neutral",
        "idle",
        0,
      );
      const x = FIELD.x + col * tileW + tileW * 0.5;
      const y = FIELD.y + row * tileH + tileH * 0.5;
      let img = pool.images[i];
      if (!img) {
        img = scene.add.image(x, y, key).setDepth(1);
        pool.images.push(img);
      } else {
        img.setTexture(key).setPosition(x, y);
      }
      img
        .setDisplaySize(tileW - 2, tileH - 2)
        .setTint(hexToColor(look.tint))
        .setAlpha(look.alpha)
        .setVisible(true)
        .setDepth(1);
      i += 1;
    }
  }
  while (pool.images.length > needed) {
    pool.images.pop()?.destroy();
  }
}
